import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePreviewStore } from "@tgpe/editor-core";

import { useTelegramWebApp } from "../../hooks/useTelegramWebApp.js";
import { logger } from "../../lib/logger.js";
import { useEditorInstance } from "../../stores/editor-instance.js";
import { useMediaStore } from "../../stores/media-store.js";
import { showToast } from "../../stores/toast-store.js";

import { copyAsMarkdownV2 } from "./copy-fallback.js";
import {
  isErrorOutcome,
  shareOutcomeMessage,
} from "./error-messages.js";
import {
  isShareMessageSupported,
  shareCurrentDraft,
} from "./share-controller.js";
import { SuccessModal } from "./SuccessModal.js";
import { useMainButton } from "./use-main-button.js";

/**
 * Owns the user-facing share flow:
 * - drives Telegram's MainButton (label, visibility, loading state)
 * - on tap, calls shareCurrentDraft and dispatches the resulting
 *   ShareOutcome to either the success modal or a toast
 * - falls back to copy-as-MarkdownV2 on Telegram clients without
 *   shareMessage support
 *
 * Mount this exactly once, on the editor screen. Hides the MainButton
 * automatically on unmount.
 */
export function ShareController() {
  const { t } = useTranslation();
  const wa = useTelegramWebApp();
  const editor = useEditorInstance((s) => s.editor);
  const previewText = usePreviewStore((s) => s.state?.text ?? "");
  const isOverLimit = usePreviewStore((s) => s.isOverLimit);
  const attachment = useMediaStore((s) => s.attachment);

  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const hasReadyMedia =
    attachment !== null && attachment.status === "ready" && !!attachment.fileId;
  const mediaUploading =
    attachment !== null && attachment.status === "uploading";
  // With media, an empty caption is a valid Telegram message (media-only post).
  const isEmpty = previewText.trim().length === 0 && !hasReadyMedia;
  const supported = isShareMessageSupported(wa);

  const handleShare = useCallback(async () => {
    if (!editor || !wa) return;
    setLoading(true);
    try {
      const media =
        attachment && attachment.status === "ready" && attachment.fileId
          ? { kind: attachment.kind, file_id: attachment.fileId }
          : undefined;
      const outcome = await shareCurrentDraft({
        editor,
        webApp: wa,
        ...(media ? { media } : {}),
      });
      if (outcome.kind === "success") {
        wa.HapticFeedback.notificationOccurred("success");
        // Keep the attachment in place — the draft is a record of what
        // was shared, and Telegram file_ids stay valid for re-use, so
        // there's no reason to wipe the slot. The user can tap × to
        // remove if they want to compose a fresh post.
        setSuccessOpen(true);
        return;
      }
      const msg = shareOutcomeMessage(outcome, t);
      if (msg) {
        showToast(msg, isErrorOutcome(outcome) ? "error" : "info");
      }
      if (isErrorOutcome(outcome)) {
        wa.HapticFeedback.notificationOccurred("error");
      }
    } finally {
      setLoading(false);
    }
  }, [editor, wa, t, attachment]);

  const handleCopyFallback = useCallback(async () => {
    if (!editor) return;
    setLoading(true);
    try {
      await copyAsMarkdownV2(editor);
      wa?.HapticFeedback.notificationOccurred("success");
      showToast(t("share.copy_success"), "success");
    } catch (err) {
      logger.error("copy fallback failed", err);
      wa?.HapticFeedback.notificationOccurred("error");
      showToast(t("error.generic"), "error");
    } finally {
      setLoading(false);
    }
  }, [editor, wa, t]);

  useMainButton(wa?.MainButton ?? null, {
    visible: !isEmpty,
    text: mediaUploading
      ? t("share.uploading_media")
      : supported
        ? t("share.main_button")
        : t("share.fallback_button"),
    enabled: !isOverLimit && !loading && !mediaUploading,
    loading: loading || mediaUploading,
    onClick: () => {
      void (supported ? handleShare() : handleCopyFallback());
    },
  });

  return (
    <SuccessModal
      open={successOpen}
      onClose={() => {
        setSuccessOpen(false);
      }}
    />
  );
}
